import {
	BorderColorOutlined,
	CheckBoxOutlined,
	CodeOutlined,
	FormatAlignCenterOutlined,
	FormatAlignJustifyOutlined,
	FormatAlignLeftOutlined,
	FormatAlignRightOutlined,
	FormatBoldOutlined,
	FormatItalicOutlined,
	FormatListBulletedOutlined,
	FormatListNumberedOutlined,
	FormatQuoteOutlined,
	FormatUnderlinedOutlined,
	InsertLink,
	IntegrationInstructionsOutlined,
	RedoOutlined,
	StrikethroughSOutlined,
	Subscript,
	Superscript,
	UndoOutlined,
} from '@mui/icons-material';
import { Box, Button, Input, SvgIcon, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Level } from '@tiptap/extension-heading';
import type { Editor } from '@tiptap/react';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { uploadImage as uploadImageAction } from '../../../../actions/aiAssistant.actions';
import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import {
	AIAssistantDisplayMode,
	aiAssistantContext,
} from '../../../../state/AIAssistantContextProvider';
import getMuiIcon from '../../../../utils/getMuiIcon';
import { useTextFormatStates, useTextMenuCommands } from '../../../../utils/tipTapEditor';
import useAction from '../../../../utils/useAction';
import useScreenSize from '../../../../utils/useScreenSize';
import ColorPickerMenu from '../../../lernsets/RichTextEditor/ContextMenu/ColorPickerMenu';
import ContextLinkMenu from '../../../lernsets/RichTextEditor/ContextMenu/ContextLinkMenu';
import useAnchorEl from '../../../lernsets/RichTextEditor/ContextMenu/hooks/useAnchor';
import TableInsertDialog from '../../../TableInsertDialog';
import { ACCEPTED_IMAGE_TYPES_STRING, DEFAULT_HEADLINE_HEIGHT } from '../constants';
import ToolbarDivider from './ToolbarDivider';
import ToolbarIconButton from './ToolbarIconButton';
import ToolbarMenuButton, { type ToolbarMenuItem } from './ToolbarMenuButton';
import { useImageUpload } from './useImageUpload';

interface AIAssistantTextEditorToolbarProps {
	editor: Editor;
}

const AIAssistantTextEditorToolbar: React.FC<AIAssistantTextEditorToolbarProps> = ({ editor }) => {
	const { t } = useTranslation();
	const { isLgScreen } = useScreenSize();
	const { displayMode } = useContext(aiAssistantContext);
	const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;

	const commands = useTextMenuCommands(editor);
	const states = useTextFormatStates(editor);

	const linkMenu = useAnchorEl();
	const highlightText = useAnchorEl();
	const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const uploadImage = useAction(uploadImageAction);
	const toggleBulletList = useCallback(() => {
		editor.chain().focus().toggleBulletList().run();
	}, [editor]);

	const toggleOrderedList = useCallback(() => {
		editor.chain().focus().toggleOrderedList().run();
	}, [editor]);

	const toggleTaskList = useCallback(() => {
		editor.chain().focus().toggleTaskList().run();
	}, [editor]);

	const toggleBlockquote = useCallback(() => {
		editor.chain().focus().toggleBlockquote().run();
	}, [editor]);

	const toggleCodeBlock = useCallback(() => {
		editor.chain().focus().toggleCodeBlock().run();
	}, [editor]);

	const toggleCode = useCallback(() => {
		editor.chain().focus().toggleCode().run();
	}, [editor]);

	const toggleSuperscript = useCallback(() => {
		editor.chain().focus().toggleSuperscript().run();
	}, [editor]);

	const toggleSubscript = useCallback(() => {
		editor.chain().focus().toggleSubscript().run();
	}, [editor]);

	const handleUndo = useCallback(() => {
		editor.chain().focus().undo().run();
	}, [editor]);

	const handleRedo = useCallback(() => {
		editor.chain().focus().redo().run();
	}, [editor]);

	const canUndo = editor.can().undo();
	const canRedo = editor.can().redo();

	const uploadImageFn = useCallback(
		async (file: File) => {
			setIsUploading(true);
			try {
				const result = await uploadImage({ image: file });
				return result;
			} finally {
				setIsUploading(false);
			}
		},
		[uploadImage]
	);

	const { fileInputRef, handleImageUploadClick, handleFileChange } = useImageUpload({
		editor,
		uploadImage: uploadImageFn,
	});

	const headingMenuItems: ToolbarMenuItem[] = useMemo(
		() => [
			{
				label: t('Paragraph'),
				selected: states.isParagraph,
				onClick: () => {
					editor.chain().focus().lift('taskItem').liftListItem('listItem').setParagraph().run();
				},
			},
			...[1, 2, 3].map((level) => ({
				label: `${t('Heading')} ${level}`,
				selected: states.isHeading(level),
				onClick: () => {
					editor
						.chain()
						.focus()
						.lift('taskItem')
						.liftListItem('listItem')
						.setHeading({ level: level as Level })
						.run();
				},
			})),
		],
		[t, states, editor]
	);

	const listMenuItems: ToolbarMenuItem[] = useMemo(
		() => [
			{
				label: t('Bullet List'),
				icon: <FormatListBulletedOutlined fontSize="small" />,
				selected: states.isBulletList,
				onClick: toggleBulletList,
			},
			{
				label: t('Numbered List'),
				icon: <FormatListNumberedOutlined fontSize="small" />,
				selected: states.isOrderedList,
				onClick: toggleOrderedList,
			},
			{
				label: t('Checklist'),
				icon: <CheckBoxOutlined fontSize="small" />,
				selected: editor.isActive('taskList'),
				onClick: toggleTaskList,
			},
		],
		[t, states, editor, toggleBulletList, toggleOrderedList, toggleTaskList]
	);

	const alignmentMenuItems: ToolbarMenuItem[] = useMemo(
		() => [
			{
				label: t('Left Aligned'),
				icon: <FormatAlignLeftOutlined fontSize="small" />,
				selected: states.isAlignLeft,
				onClick: commands.onAlignLeft,
			},
			{
				label: t('Centered'),
				icon: <FormatAlignCenterOutlined fontSize="small" />,
				selected: states.isAlignCenter,
				onClick: commands.onAlignCenter,
			},
			{
				label: t('Right Aligned'),
				icon: <FormatAlignRightOutlined fontSize="small" />,
				selected: states.isAlignRight,
				onClick: commands.onAlignRight,
			},
			{
				label: t('Justify'),
				icon: <FormatAlignJustifyOutlined fontSize="small" />,
				selected: states.isAlignJustify,
				onClick: commands.onAlignJustify,
			},
		],
		[t, states, commands]
	);

	const scriptMenuItems: ToolbarMenuItem[] = useMemo(
		() => [
			{
				label: t('Superscript'),
				icon: <Superscript fontSize="small" />,
				selected: editor.isActive('superscript'),
				onClick: toggleSuperscript,
			},
			{
				label: t('Subscript'),
				icon: <Subscript fontSize="small" />,
				selected: editor.isActive('subscript'),
				onClick: toggleSubscript,
			},
		],
		[t, editor, toggleSuperscript, toggleSubscript]
	);

	return (
		<Box
			data-testid="ai-assistant-text-editor-toolbar"
			sx={{
				display: 'flex',
				flexDirection: 'row',
				gap: spaces.xxs,
				rowGap: spaces.sm,
				padding: spaces.xs,
				borderBottom: `1px solid ${palette.primary.contrast}`,
				width: '100%',
				height: DEFAULT_HEADLINE_HEIGHT,
				flexWrap: 'wrap',
			}}
		>
			<ToolbarIconButton
				title={t('Undo')}
				dataTestId="undo"
				icon={<UndoOutlined />}
				onClick={handleUndo}
				disabled={!canUndo}
			/>
			<ToolbarIconButton
				title={t('Redo')}
				dataTestId="redo"
				icon={<RedoOutlined />}
				onClick={handleRedo}
				disabled={!canRedo}
			/>
			<ToolbarDivider />
			<ToolbarMenuButton
				title={t('Text Format')}
				dataTestId="heading-select-button"
				label="H"
				menuItems={headingMenuItems}
			/>
			<ToolbarDivider />
			<ToolbarMenuButton
				title={t('Lists')}
				dataTestId="list-menu"
				startIcon={<FormatListBulletedOutlined />}
				isActive={states.isBulletList || states.isOrderedList || editor.isActive('taskList')}
				menuItems={listMenuItems}
			/>
			<ToolbarIconButton
				title={t('Blockquote')}
				dataTestId="blockquote"
				icon={<FormatQuoteOutlined />}
				onClick={toggleBlockquote}
				isActive={editor.isActive('blockquote')}
			/>
			<ToolbarIconButton
				title={t('Code Block')}
				dataTestId="code-block"
				icon={<IntegrationInstructionsOutlined />}
				onClick={toggleCodeBlock}
				isActive={editor.isActive('codeBlock')}
			/>
			<ToolbarDivider />
			<ToolbarIconButton
				title={t('Bold')}
				dataTestId="bold"
				icon={<FormatBoldOutlined />}
				onClick={commands.onBold}
				isActive={states.isBold}
			/>
			<ToolbarIconButton
				title={t('Italic')}
				dataTestId="italic"
				icon={<FormatItalicOutlined />}
				onClick={commands.onItalic}
				isActive={states.isItalic}
			/>
			<ToolbarIconButton
				title={t('Strikethrough')}
				dataTestId="strike"
				icon={<StrikethroughSOutlined />}
				onClick={commands.onStrike}
				isActive={states.isStrike}
			/>
			<ToolbarIconButton
				title={t('Inline Code')}
				dataTestId="code"
				icon={<CodeOutlined />}
				onClick={toggleCode}
				isActive={editor.isActive('code')}
			/>
			<ToolbarIconButton
				title={t('Underline')}
				dataTestId="underline"
				icon={<FormatUnderlinedOutlined />}
				onClick={commands.onUnderline}
				isActive={states.isUnderline}
			/>
			<ColorPickerMenu
				id={t('Highlight')}
				dataTestId="text-highlight"
				icon={<BorderColorOutlined />}
				anchorEl={highlightText.anchorEl}
				handleClick={highlightText.handleClick}
				handleClose={highlightText.handleClose}
				currentColor={states.currentHighlight}
				onChangeColor={commands.onChangeHighlight}
				onClearColor={() => {
					commands.onClearHighlight();
					highlightText.handleClose();
				}}
			/>
			<ToolbarDivider />
			<ContextLinkMenu
				editor={editor}
				id={t('Link')}
				anchorEl={linkMenu.anchorEl}
				handleClick={linkMenu.handleClick}
				handleClose={linkMenu.handleClose}
				icon={<InsertLink />}
			/>
			<ToolbarDivider />
			<ToolbarMenuButton
				title={t('Script')}
				dataTestId="script-menu"
				startIcon={<Superscript />}
				isActive={editor.isActive('superscript') || editor.isActive('subscript')}
				menuItems={scriptMenuItems}
			/>
			<ToolbarDivider />
			<ToolbarMenuButton
				title={t('Alignment')}
				dataTestId="alignment-menu"
				startIcon={<FormatAlignLeftOutlined />}
				isActive={states.isAlignCenter || states.isAlignRight || states.isAlignJustify}
				menuItems={alignmentMenuItems}
			/>
			<ToolbarDivider />
			<ToolbarIconButton
				title={t('Table')}
				dataTestId="table"
				icon={<SvgIcon component={getMuiIcon('table')} />}
				onClick={() => setIsTableDialogOpen(true)}
				isActive={editor.isActive('table')}
			/>
			{isLgScreen && isPageMode ? (
				<Tooltip title={t('Image')} placement="top">
					<Button
						data-testid="image"
						size="small"
						color="secondary"
						variant="outlined"
						startIcon={<SvgIcon component={getMuiIcon('galleryOutlined')} />}
						onClick={handleImageUploadClick}
						disabled={isUploading}
						sx={{
							minWidth: '48px',
							padding: `${spaces.xxs} ${spaces.md}`,
							backgroundColor: 'transparent',
							'&:hover': {
								backgroundColor: alpha(palette.primary.main, 0.06),
							},
						}}
					>
						<Typography fontWeight={500}>{t('Image')}</Typography>
					</Button>
				</Tooltip>
			) : (
				<ToolbarIconButton
					title={t('Image')}
					dataTestId="image"
					icon={<SvgIcon component={getMuiIcon('gallery')} />}
					onClick={handleImageUploadClick}
					disabled={isUploading}
				/>
			)}
			<Input
				data-testid="file-input"
				sx={{ display: 'none' }}
				inputRef={fileInputRef}
				type="file"
				inputProps={{
					accept: ACCEPTED_IMAGE_TYPES_STRING,
					'data-testid': 'ai-assistant-toolbar-file-input',
				}}
				onChange={handleFileChange}
			/>
			<TableInsertDialog
				open={isTableDialogOpen}
				onClose={() => setIsTableDialogOpen(false)}
				editor={editor}
			/>
		</Box>
	);
};

export default AIAssistantTextEditorToolbar;
